import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   Consulta e Participação dos Trabalhadores (SST)
   Construtor de formulários + partilha de link + resultados.
   ============================================================ */

const TIPO_LABEL = { geral: 'Geral', dl50: 'DL 50/2005', iso45001: 'ISO 45001', satisfacao: 'Satisfação', custom: 'Personalizada' };
const ESTADO_INFO = {
    rascunho: { label: 'Rascunho', bg: '#f1f5f9', fg: '#475569' },
    ativo: { label: 'Ativa', bg: '#dcfce7', fg: '#166534' },
    fechado: { label: 'Fechada', bg: '#fee2e2', fg: '#991b1b' },
};
const PRESETS = {
    'Sim / Não': ['Sim', 'Não'],
    'Sim / Não / Não sabe': ['Sim', 'Não', 'Não sabe'],
    'Sim / Não / Pode Melhorar': ['Sim', 'Não', 'Pode Melhorar'],
};
const emptyPergunta = () => ({ texto: '', tema: '', tipo: 'single', opcoes: ['Sim', 'Não'], obrigatoria: true });
const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

/* ---------- Construtor / edição ---------- */
function BuilderModal({ consultaId, ano, alvo = 'trabalhadores', onClose, onSaved }) {
    const novo = consultaId == null;
    const [titulo, setTitulo] = useState('');
    const [descricao, setDescricao] = useState('');
    // Inquéritos a clientes são identificados por omissão; consultas SST anónimas.
    const [anonima, setAnonima] = useState(alvo !== 'clientes');
    const [perguntas, setPerguntas] = useState([emptyPergunta()]);
    const [bloqueada, setBloqueada] = useState(false); // tem respostas → perguntas não editáveis
    const [loading, setLoading] = useState(!novo);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (novo) return;
        (async () => {
            try {
                const [c, r] = await Promise.all([
                    api.get(`/consultas/${consultaId}`),
                    api.get(`/consultas/${consultaId}/resultados`),
                ]);
                setTitulo(c.data.titulo || '');
                setDescricao(c.data.descricao || '');
                setAnonima(c.data.anonima !== false);
                setPerguntas((c.data.perguntas || []).map(p => ({
                    texto: p.texto, tema: p.tema || '', tipo: p.tipo, opcoes: Array.isArray(p.opcoes) ? p.opcoes : [], obrigatoria: p.obrigatoria !== false,
                })));
                setBloqueada((r.data.num_respostas || 0) > 0);
            } catch (e) {
                setErr('Erro ao carregar consulta');
            } finally { setLoading(false); }
        })();
    }, [consultaId, novo]);

    const upd = (i, patch) => setPerguntas(prev => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const mover = (i, dir) => setPerguntas(prev => {
        const j = i + dir;
        if (j < 0 || j >= prev.length) return prev;
        const arr = [...prev];[arr[i], arr[j]] = [arr[j], arr[i]];
        return arr;
    });
    const remover = (i) => setPerguntas(prev => prev.filter((_, idx) => idx !== i));
    const addPergunta = () => setPerguntas(prev => [...prev, emptyPergunta()]);

    const setOpcao = (i, oi, val) => upd(i, { opcoes: perguntas[i].opcoes.map((o, idx) => idx === oi ? val : o) });
    const addOpcao = (i) => upd(i, { opcoes: [...perguntas[i].opcoes, ''] });
    const removeOpcao = (i, oi) => upd(i, { opcoes: perguntas[i].opcoes.filter((_, idx) => idx !== oi) });

    const guardar = async () => {
        if (!titulo.trim()) { setErr('Indique o título da consulta.'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { titulo, descricao, anonima, perguntas };
            if (novo) await api.post('/consultas', { ...payload, ano, publico_alvo: alvo });
            else await api.put(`/consultas/${consultaId}`, payload);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? 'Nova consulta' : 'Editar consulta'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                {loading ? <p style={{ color: '#64748b' }}>A carregar…</p> : (
                    <>
                        {bloqueada && (
                            <div style={infoBox}>Esta consulta já tem respostas. O título e definições podem ser alterados, mas as <strong>perguntas estão bloqueadas</strong> para preservar os resultados.</div>
                        )}
                        <div style={{ marginBottom: 12 }}>
                            <label data-liquid-label="true" style={lbl}>Título *</label>
                            <input value={titulo} onChange={e => setTitulo(e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label data-liquid-label="true" style={lbl}>Descrição</label>
                            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                            <input type="checkbox" checked={anonima} onChange={e => setAnonima(e.target.checked)} />
                            Respostas anónimas
                        </label>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <strong style={{ color: '#334155' }}>Perguntas ({perguntas.length})</strong>
                        </div>

                        {perguntas.map((pg, i) => (
                            <div key={i} style={pgCard}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 700, color: '#3182ce', paddingTop: 8 }}>{i + 1}.</span>
                                    <textarea value={pg.texto} disabled={bloqueada} placeholder="Texto da pergunta"
                                        onChange={e => upd(i, { texto: e.target.value })} rows={2}
                                        style={{ ...inp, flex: 1, resize: 'vertical', opacity: bloqueada ? 0.6 : 1 }} />
                                    {!bloqueada && (
                                        <div data-liquid-actions="true" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <button onClick={() => mover(i, -1)} style={miniBtn} title="Subir">▲</button>
                                            <button onClick={() => mover(i, 1)} style={miniBtn} title="Descer">▼</button>
                                            <button onClick={() => remover(i)} style={{ ...miniBtn, color: '#e53e3e' }} title="Remover"><DeleteIcon /></button>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0 0 26px', flexWrap: 'wrap' }}>
                                    <select value={pg.tipo} disabled={bloqueada}
                                        onChange={e => upd(i, { tipo: e.target.value, opcoes: e.target.value === 'text' ? [] : (pg.opcoes.length ? pg.opcoes : ['Sim', 'Não']) })}
                                        style={inp}>
                                        <option value="single">Escolha única</option>
                                        <option value="multiple">Escolha múltipla</option>
                                        <option value="text">Resposta aberta</option>
                                    </select>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#475569' }}>
                                        <input type="checkbox" checked={pg.obrigatoria} disabled={bloqueada}
                                            onChange={e => upd(i, { obrigatoria: e.target.checked })} /> Obrigatória
                                    </label>
                                    <input value={pg.tema || ''} disabled={bloqueada} onChange={e => upd(i, { tema: e.target.value })}
                                        placeholder="Tema (opcional)" title="Aparece por cima da pergunta, no formulário e nos resultados"
                                        style={{ ...inp, width: 230, fontSize: '0.82rem', opacity: bloqueada ? 0.6 : 1 }} />
                                </div>

                                {pg.tipo !== 'text' && !bloqueada && (
                                    <div style={{ margin: '8px 0 0 26px' }}>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                                            {Object.entries(PRESETS).map(([label, ops]) => (
                                                <button key={label} onClick={() => upd(i, { opcoes: [...ops] })} style={presetBtn}><LiquidText>{label}</LiquidText></button>
                                            ))}
                                        </div>
                                        {pg.opcoes.map((o, oi) => (
                                            <div key={oi} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                                                <input value={o} onChange={e => setOpcao(i, oi, e.target.value)} style={{ ...inp, flex: 1 }} placeholder={`Opção ${oi + 1}`} />
                                                <button onClick={() => removeOpcao(i, oi)} style={{ ...miniBtn, color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </div>
                                        ))}
                                        <button onClick={() => addOpcao(i)} style={presetBtn}>+ Opção</button>
                                    </div>
                                )}
                                {pg.tipo !== 'text' && bloqueada && (
                                    <div style={{ margin: '6px 0 0 26px', fontSize: '0.82rem', color: '#94a3b8' }}>{pg.opcoes.join(' · ')}</div>
                                )}
                            </div>
                        ))}

                        {!bloqueada && <button data-liquid-secondary="true" onClick={addPergunta} style={{ ...btnSec, marginTop: 4 }}>+ Adicionar pergunta</button>}

                        {err && <div style={{ color: '#e53e3e', margin: '12px 0', fontSize: '0.85rem' }}>{err}</div>}

                        <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                            <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                            <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'A guardar…' : 'Guardar'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Partilha do link ---------- */
function PartilhaModal({ consulta, alvo = 'trabalhadores', onClose, onChanged }) {
    const ehClientes = alvo === 'clientes';
    // O link vem do servidor com o domínio público: o de window.location, se a
    // app estiver aberta na rede local (192.168…), não abre no telemóvel.
    const [link, setLink] = useState('');
    const [dest, setDest] = useState(null); // trabalhadores: { trabalhadores: [{nome,email,enviado_em}], sem_email: [] }
    const [mensagem, setMensagem] = useState('Pedimos a sua colaboração no preenchimento desta consulta. A sua participação é importante.');
    const [copiado, setCopiado] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [resultadoEnvio, setResultadoEnvio] = useState('');

    const carregarPartilha = useCallback(async () => {
        try {
            const r = await api.get(`/consultas/${consulta.id}/partilha`, { params: { origem: window.location.origin } });
            setLink(r.data.link || `${window.location.origin}/consulta/${consulta.token}`);
            if (!ehClientes) setDest({ trabalhadores: r.data.trabalhadores || [], sem_email: r.data.sem_email || [] });
        } catch {
            setLink(`${window.location.origin}/consulta/${consulta.token}`);
        }
    }, [consulta.id, consulta.token, ehClientes]);
    useEffect(() => { carregarPartilha(); }, [carregarPartilha]);

    const porEnviar = dest ? dest.trabalhadores.filter(t => !t.enviado_em) : [];
    const jaReceberam = dest ? dest.trabalhadores.length - porEnviar.length : 0;
    // Destinatários (só clientes): emails registados selecionáveis + emails colados
    const [registados, setRegistados] = useState([]);
    const [sel, setSel] = useState(() => new Set());
    const [filtroDest, setFiltroDest] = useState('');
    const [colados, setColados] = useState('');

    useEffect(() => {
        if (!ehClientes) return;
        api.get('/consultas/destinatarios-clientes')
            .then(r => setRegistados(Array.isArray(r.data) ? r.data : []))
            .catch(() => setRegistados([]));
    }, [ehClientes]);

    const toggle = (email) => setSel(prev => {
        const n = new Set(prev);
        if (n.has(email)) n.delete(email); else n.add(email);
        return n;
    });
    const emailsColados = colados.split(/[\s,;]+/).map(e => e.trim()).filter(e => /\S+@\S+\.\S+/.test(e));
    const totalDest = sel.size + emailsColados.filter(e => !sel.has(e.toLowerCase())).length;
    const registadosVisiveis = filtroDest.trim()
        ? registados.filter(d => (d.nome || '').toLowerCase().includes(filtroDest.trim().toLowerCase()) || (d.email || '').includes(filtroDest.trim().toLowerCase()))
        : registados;

    const copiar = async () => {
        try { await navigator.clipboard.writeText(link); setCopiado(true); setTimeout(() => setCopiado(false), 2000); }
        catch { window.prompt('Copie o link:', link); }
    };
    const whatsapp = () => {
        const txt = encodeURIComponent(`${mensagem}\n\n${link}`);
        window.open(`https://wa.me/?text=${txt}`, '_blank');
    };
    // Trabalhadores: modo 'por_enviar' (quem ainda não recebeu) ou 'todos' (reenvio).
    const enviarEmail = async (modo = 'por_enviar') => {
        if (!ehClientes && modo === 'todos'
            && !window.confirm(`Reenviar o email aos ${dest?.trabalhadores.length || 0} trabalhadores com email, incluindo quem já o recebeu?`)) return;
        setEnviando(true); setResultadoEnvio('');
        try {
            const body = { mensagem, base_url: window.location.origin };
            if (ehClientes) {
                const destinatarios = [...new Set([...sel, ...emailsColados.map(e => e.toLowerCase())])];
                if (destinatarios.length === 0) {
                    setResultadoEnvio('Selecione ou cole pelo menos um email.');
                    setEnviando(false);
                    return;
                }
                body.destinatarios = destinatarios;
            } else {
                body.modo = modo;
            }
            // Um email por trabalhador: com 20 destinatários passa bem dos 15 s por omissão.
            const res = await api.post(`/consultas/${consulta.id}/enviar-email`, body, { timeout: 180000 });
            setResultadoEnvio(res.data.message || 'Enviado.');
            onChanged && onChanged();
        } catch (e) {
            setResultadoEnvio(e.response?.data?.error || 'Erro ao enviar.');
        } finally {
            setEnviando(false);
            if (!ehClientes) carregarPartilha();
        }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: ehClientes ? 620 : 540 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{ehClientes ? 'Enviar inquérito aos clientes' : 'Partilhar consulta'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 12 }}><LiquidText>{consulta.titulo}</LiquidText></div>

                <label data-liquid-label="true" style={lbl}>Link público</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                    <input readOnly value={link || 'A obter o link…'} onFocus={e => e.target.select()} style={{ ...inp, flex: 1, background: '#f8fafc' }} />
                    <button data-liquid-secondary="true" onClick={copiar} disabled={!link} style={btnSec}><LiquidText>{copiado ? '✓ Copiado' : 'Copiar'}</LiquidText></button>
                </div>

                {!ehClientes && dest && (
                    <>
                        <label data-liquid-label="true" style={lbl}>
                            Por email — {dest.trabalhadores.length === 0 ? 'nenhum trabalhador com email'
                                : `${porEnviar.length} por enviar · ${jaReceberam} já receberam`}
                        </label>
                        {dest.trabalhadores.length > 0 && (
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 210, overflowY: 'auto', marginBottom: 8 }}>
                                {dest.trabalhadores.map(t => (
                                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f8fafc', fontSize: '0.84rem' }}>
                                        <span style={{ flex: 1, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.email}>{t.nome}</span>
                                        <span data-liquid-tone={liquidTone(t.enviado_em ? '#dcfce7' : '#fef3c7')} data-liquid-badge="true" style={{
                                            ...badge, fontSize: '0.7rem',
                                            background: t.enviado_em ? '#dcfce7' : '#fef3c7', color: t.enviado_em ? '#166534' : '#92400e',
                                        }}><LiquidText>{t.enviado_em ? `✓ ${fmtData(t.enviado_em)}` : 'por enviar'}</LiquidText></span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {dest.sem_email.length > 0 && (
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 14 }}
                                title={dest.sem_email.map(t => t.nome).join('\n')}>
                                {dest.sem_email.length} trabalhador{dest.sem_email.length === 1 ? '' : 'es'} sem email na ficha — para esses, o link segue pelo WhatsApp.
                            </div>
                        )}
                    </>
                )}

                {ehClientes && (
                    <>
                        <label data-liquid-label="true" style={lbl}>Emails registados ({sel.size} selecionado{sel.size === 1 ? '' : 's'})</label>
                        <input value={filtroDest} onChange={e => setFiltroDest(e.target.value)} placeholder="Pesquisar cliente…"
                            style={{ ...inp, width: '100%', marginBottom: 6 }} />
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 190, overflowY: 'auto', marginBottom: 12 }}>
                            {registadosVisiveis.length === 0 ? (
                                <div style={{ padding: 12, color: '#94a3b8', fontSize: '0.85rem' }}>Sem emails registados.</div>
                            ) : registadosVisiveis.map(d => (
                                <label key={d.email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', fontSize: '0.85rem' }}>
                                    <input type="checkbox" checked={sel.has(d.email)} onChange={() => toggle(d.email)} />
                                    <span style={{ flex: 1, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nome}</span>
                                    <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{d.email}</span>
                                </label>
                            ))}
                        </div>

                        <label data-liquid-label="true" style={lbl}>Outros emails (colar — separados por vírgula, espaço ou linha)</label>
                        <textarea value={colados} onChange={e => setColados(e.target.value)} rows={2}
                            placeholder="email1@exemplo.pt, email2@exemplo.pt"
                            style={{ ...inp, width: '100%', resize: 'vertical', marginBottom: 12 }} />
                    </>
                )}

                <label data-liquid-label="true" style={lbl}>Mensagem</label>
                <textarea value={mensagem} onChange={e => setMensagem(e.target.value)} rows={3} style={{ ...inp, width: '100%', resize: 'vertical', marginBottom: 14 }} />

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button data-liquid-primary="true" onClick={whatsapp} disabled={!link} style={{ ...btnPri, background: '#25D366', border: 'none' }}>WhatsApp</button>
                    {ehClientes ? (
                        <button data-liquid-primary="true" onClick={() => enviarEmail()} disabled={enviando} style={{ ...btnPri, opacity: enviando ? 0.6 : 1 }}>
                            <LiquidText>{enviando ? 'A enviar…' : `✉ Enviar (${totalDest})`}</LiquidText>
                        </button>
                    ) : (
                        <>
                            <button data-liquid-primary="true" onClick={() => enviarEmail('por_enviar')} disabled={enviando || !dest || porEnviar.length === 0}
                                style={{ ...btnPri, opacity: (enviando || !dest || porEnviar.length === 0) ? 0.6 : 1 }}>
                                <LiquidText>{enviando ? 'A enviar…' : porEnviar.length ? `✉ Enviar aos que ainda não receberam (${porEnviar.length})` : '✉ Todos já receberam'}</LiquidText>
                            </button>
                            {jaReceberam > 0 && (
                                <button data-liquid-secondary="true" onClick={() => enviarEmail('todos')} disabled={enviando} style={btnSec}><LiquidText>↻ Reenviar a todos</LiquidText></button>
                            )}
                        </>
                    )}
                </div>
                {resultadoEnvio && <div style={{ marginTop: 10, fontSize: '0.85rem', color: '#475569' }}>{resultadoEnvio}</div>}
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 12 }}>
                    {ehClientes
                        ? 'O email é enviado em BCC aos destinatários selecionados e/ou colados.'
                        : 'Cada trabalhador ativo com email na ficha recebe o seu email, e fica registado. Quem entrar depois ou passar a ter email aparece como «por enviar».'}
                </p>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Resultados ---------- */
function ResultadosModal({ consultaId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState('');

    useEffect(() => {
        (async () => {
            try { const r = await api.get(`/consultas/${consultaId}/resultados`); setData(r.data); }
            finally { setLoading(false); }
        })();
    }, [consultaId]);

    const exportar = async (formato) => {
        setExporting(formato);
        try {
            const ext = formato === 'excel' ? 'xlsx' : 'pdf';
            const mime = formato === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
            const res = await api.get(`/consultas/${consultaId}/resultados/${formato}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `resultados_consulta.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert('Erro ao exportar.');
        } finally { setExporting(''); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div className="consult-results-shell" style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Resultados</h3>
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button data-liquid-secondary="true" onClick={() => exportar('excel')} disabled={!!exporting || !data?.num_respostas} style={{ ...btnSec, ...mini }} title="Exportar para Excel">
                            <LiquidText>{exporting === 'excel' ? '…' : '⬇ Excel'}</LiquidText>
                        </button>
                        <button data-liquid-secondary="true" onClick={() => exportar('pdf')} disabled={!!exporting || !data?.num_respostas} style={{ ...btnSec, ...mini }} title="Exportar para PDF">
                            <LiquidText>{exporting === 'pdf' ? '…' : '⬇ PDF'}</LiquidText>
                        </button>
                        <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                    </div>
                </div>
                <div className="consult-results-scroll">
                {loading ? <p style={{ color: '#64748b' }}>A carregar…</p> : !data ? <p>Sem dados.</p> : (
                    <>
                        <div style={{ marginBottom: 14, color: '#334155' }}>
                            <strong><LiquidText>{data.consulta.titulo}</LiquidText></strong>
                            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>{data.num_respostas} resposta(s)</div>
                        </div>
                        {data.resultados.map((r, idx) => (
                            <div key={r.pergunta_id} style={{ padding: '12px 0', borderTop: '1px solid #f1f5f9' }}>
                                {r.tema && <div style={temaTag}>{r.tema}</div>}
                                <div style={{ fontWeight: 600, color: '#334155', marginBottom: 8, fontSize: '0.92rem' }}>
                                    <span style={{ color: '#3182ce' }}>{idx + 1}.</span> <LiquidText>{r.texto}</LiquidText>
                                </div>
                                {r.tipo === 'text' ? (
                                    r.textos.length === 0 ? <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Sem respostas.</div> : (
                                        <ul style={{ margin: 0, paddingLeft: 18, color: '#475569', fontSize: '0.88rem' }}>
                                            {r.textos.map((t, i) => <li key={i} style={{ marginBottom: 3 }}>“{t}”</li>)}
                                        </ul>
                                    )
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        {r.opcoes.map(opt => {
                                            const n = r.contagem[opt] || 0;
                                            const pct = r.total ? Math.round((n / r.total) * 100) : 0;
                                            return (
                                                <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                                                    <span style={{ width: 150, color: '#475569', flexShrink: 0 }}>{opt}</span>
                                                    <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 16, overflow: 'hidden' }}>
                                                        <div style={{ width: `${pct}%`, background: '#3182ce', height: '100%' }} />
                                                    </div>
                                                    <span style={{ width: 56, textAlign: 'right', color: '#334155', fontWeight: 600 }}>{n} ({pct}%)</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                )}
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Assistente de importação de resultados (histórico) ---------- */
function ImportWizard({ ano, onClose, onSaved }) {
    const [tipo, setTipo] = useState('');
    const [perguntas, setPerguntas] = useState([]);
    const [titulo, setTitulo] = useState('');
    const [participacoes, setParticipacoes] = useState('');
    const [contagens, setContagens] = useState([]);
    const [anoImp, setAnoImp] = useState(ano);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const escolherTipo = async (t) => {
        setTipo(t); setErr('');
        if (!t) { setPerguntas([]); setContagens([]); return; }
        setLoading(true);
        try {
            const r = await api.get(`/consultas/importar/template/${t}`);
            setPerguntas(r.data.perguntas || []);
            setTitulo(r.data.titulo || '');
            setContagens((r.data.perguntas || []).map(pg => pg.tipo === 'text' ? { __text: [] } : {}));
        } catch (e) { setErr('Erro ao carregar modelo'); }
        finally { setLoading(false); }
    };

    const setCont = (i, opt, val) => setContagens(prev => prev.map((c, idx) => idx === i ? { ...c, [opt]: val === '' ? 0 : parseInt(val, 10) || 0 } : c));
    const setTextos = (i, val) => setContagens(prev => prev.map((c, idx) => idx === i ? { __text: val.split('\n').map(s => s.trim()).filter(Boolean) } : c));

    const guardar = async () => {
        if (!tipo) { setErr('Escolha o modelo.'); return; }
        if (!participacoes || parseInt(participacoes, 10) < 1) { setErr('Indique o nº de participações.'); return; }
        setSaving(true); setErr('');
        try {
            await api.post('/consultas/importar', { template: tipo, ano: anoImp, titulo, participacoes: parseInt(participacoes, 10), contagens });
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao importar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Importar resultados (histórico)</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0 }}>
                    Registe os resultados agregados de uma consulta já realizada (ex.: a partir de um PDF). Escreva as contagens de cada opção.
                </p>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Modelo</label>
                        <select value={tipo} onChange={e => escolherTipo(e.target.value)} style={inp}>
                            <option value="">— Selecionar —</option>
                            <option value="geral">Consulta Geral (SST)</option>
                            <option value="dl50">DL 50/2005</option>
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <input type="number" value={anoImp} onChange={e => setAnoImp(parseInt(e.target.value, 10))} style={{ ...inp, width: 90 }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nº de participações</label>
                        <input type="number" value={participacoes} onChange={e => setParticipacoes(e.target.value)} style={{ ...inp, width: 110 }} placeholder="ex.: 5" />
                    </div>
                </div>

                {tipo && (
                    <div style={{ marginBottom: 12 }}>
                        <label data-liquid-label="true" style={lbl}>Título</label>
                        <input value={titulo} onChange={e => setTitulo(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                )}

                {loading ? <p style={{ color: '#64748b' }}>A carregar modelo…</p> : perguntas.map((pg, i) => (
                    <div key={i} style={pgCard}>
                        <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.88rem', marginBottom: 6 }}>
                            <span style={{ color: '#3182ce' }}>{i + 1}.</span> <LiquidText>{pg.texto}</LiquidText>
                        </div>
                        {pg.tipo === 'text' ? (
                            <textarea rows={2} placeholder="Respostas abertas (uma por linha) — opcional"
                                onChange={e => setTextos(i, e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                        ) : (
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {pg.opcoes.map(opt => (
                                    <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: '0.82rem', color: '#475569' }}>{opt}</span>
                                        <input type="number" min="0" value={contagens[i]?.[opt] ?? ''} onChange={e => setCont(i, opt, e.target.value)}
                                            style={{ ...inp, width: 64 }} placeholder="0" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {err && <div style={{ color: '#e53e3e', margin: '12px 0', fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving || !tipo} style={{ ...btnPri, opacity: (saving || !tipo) ? 0.6 : 1 }}>
                        {saving ? 'A importar…' : 'Importar resultados'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ----------
   alvo='trabalhadores' (consultas SST) | 'clientes' (satisfação do cliente,
   ISO 9001 9.1.2) — mesmo motor, público e destinatários diferentes. */
const ConsultaTrabalhadores = ({ alvo = 'trabalhadores' }) => {
    const ehClientes = alvo === 'clientes';
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [consultas, setConsultas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [builder, setBuilder] = useState(null); // {id|null}
    const [partilha, setPartilha] = useState(null);
    const [resultados, setResultados] = useState(null);
    const [criarMenu, setCriarMenu] = useState(false);
    const [importar, setImportar] = useState(false);
    const [resumoAnual, setResumoAnual] = useState([]);

    const carregar = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const [r, ra] = await Promise.all([
                api.get('/consultas', { params: { ano, alvo } }),
                api.get('/consultas/resumo-anual', { params: { alvo } }),
            ]);
            setConsultas(r.data || []);
            setResumoAnual(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) {
            setErro('Não foi possível carregar as consultas.');
        } finally { setLoading(false); }
    }, [ano, alvo]);

    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 6; a--) anos.push(a);

    const criarTemplate = async (template) => {
        setCriarMenu(false);
        try {
            const r = await api.post('/consultas', { template, ano, publico_alvo: alvo, anonima: !ehClientes });
            carregar();
            setBuilder({ id: r.data.id });
        } catch (e) { alert(e.response?.data?.error || 'Erro ao criar a partir do modelo'); }
    };

    const mudarEstado = async (c, estado) => {
        try {
            const r = await api.post(`/consultas/${c.id}/estado`, { estado });
            carregar();
            // Consulta a trabalhadores acabada de lançar: segue logo para a
            // partilha (email a quem tem, link para o grupo de WhatsApp).
            if (estado === 'ativo' && c.estado === 'rascunho' && !ehClientes) setPartilha({ ...c, ...r.data });
        } catch (e) { alert(e.response?.data?.error || 'Erro ao alterar estado'); }
    };

    const eliminar = async (c) => {
        if (!window.confirm(`Eliminar a consulta "${c.titulo}" e todas as respostas?`)) return;
        try { await api.delete(`/consultas/${c.id}`); carregar(); }
        catch (e) { alert('Erro ao eliminar'); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.9rem', paddingBottom: 6 }}>
                        {ehClientes
                            ? 'Crie inquéritos de satisfação, envie o link aos clientes e acompanhe os resultados.'
                            : 'Crie consultas SST, partilhe o link com os trabalhadores e veja os resultados.'}
                    </div>
                </div>
                <div style={{ position: 'relative' }}>
                    <button data-liquid-primary="true" onClick={() => setCriarMenu(v => !v)} style={btnPri}>{ehClientes ? '+ Novo inquérito ▾' : '+ Nova consulta ▾'}</button>
                    {criarMenu && (
                        <div style={dropdown}>
                            <button style={dropItem} onClick={() => { setCriarMenu(false); setBuilder({ id: null }); }}>Em branco</button>
                            {ehClientes ? (
                                <button style={dropItem} onClick={() => criarTemplate('satisfacao')}>Modelo: Inquérito de Satisfação</button>
                            ) : (
                                <>
                                    <button style={dropItem} onClick={() => criarTemplate('geral')}>Modelo: Consulta Geral (SST)</button>
                                    <button style={dropItem} onClick={() => criarTemplate('iso45001')}>Modelo: ISO 45001 — consulta e participação</button>
                                    <button style={dropItem} onClick={() => criarTemplate('dl50')}>Modelo: DL 50/2005</button>
                                    <div style={{ borderTop: '1px solid #eef2f7' }} />
                                    <button style={dropItem} onClick={() => { setCriarMenu(false); setImportar(true); }}><LiquidText>⬆ Importar resultados (histórico)…</LiquidText></button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Comparativo de participações por ano */}
            {resumoAnual.length > 0 && (() => {
                const maxP = Math.max(1, ...resumoAnual.map(r => r.participacoes || 0));
                return (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                            Participações por ano
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {resumoAnual.map(r => {
                                const sel = r.ano === ano;
                                const pct = Math.round(((r.participacoes || 0) / maxP) * 100);
                                return (
                                    <div key={r.ano} onClick={() => setAno(r.ano)} title={`${r.num_consultas} consulta(s)`}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                        <span style={{ width: 46, fontWeight: sel ? 700 : 500, color: sel ? '#1e40af' : '#475569', fontSize: '0.9rem' }}>{r.ano}</span>
                                        <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 20, overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, minWidth: r.participacoes ? 2 : 0, background: sel ? '#3182ce' : '#93c5fd', height: '100%', transition: 'width .2s' }} />
                                        </div>
                                        <span style={{ width: 130, textAlign: 'right', fontSize: '0.82rem', color: '#334155' }}>
                                            <strong>{r.participacoes || 0}</strong> particip. · {r.num_consultas} consulta{r.num_consultas === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : erro ? <p style={{ color: '#e53e3e' }}>{erro}</p>
                    : consultas.length === 0 ? (
                        <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                            Sem consultas em {ano}. Crie uma nova ou parta de um modelo.
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                            <table className="standard-table">
                                <thead>
                                    <tr>
                                        <th>Consulta</th>
                                        <th>Modelo</th>
                                        <th>Lançada em</th>
                                        <th style={{ textAlign: 'center' }}>Perguntas</th>
                                        <th style={{ textAlign: 'center' }}>Respostas</th>
                                        <th>Estado</th>
                                        <th style={{ textAlign: 'right' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {consultas.map(c => {
                                        const ei = ESTADO_INFO[c.estado] || ESTADO_INFO.rascunho;
                                        const participacoes = c.importada ? (c.participacoes || 0) : Number(c.num_respostas || 0);
                                        return (
                                            <tr key={c.id}>
                                                <td style={{ fontWeight: 600, maxWidth: 320 }}><LiquidText>{c.titulo}</LiquidText></td>
                                                <td>
                                                    {TIPO_LABEL[c.tipo] || c.tipo}
                                                    {c.importada && <span data-liquid-tone={liquidTone('#ede9fe')} data-liquid-badge="true" style={{ ...badge, background: '#ede9fe', color: '#6d28d9', marginLeft: 6, fontSize: '0.7rem' }}>Histórico</span>}
                                                </td>
                                                <td style={{ color: '#64748b' }}>{fmtData(c.ativada_em || c.criado_em)}</td>
                                                <td style={{ textAlign: 'center' }}>{c.num_perguntas}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 600 }}>{participacoes}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <span data-liquid-tone={liquidTone(ei.bg)} data-liquid-badge="true" style={{ ...badge, background: ei.bg, color: ei.fg }}><LiquidText>{ei.label}</LiquidText></span>
                                                    {!ehClientes && c.num_envios > 0 && (
                                                        <span title="Trabalhadores que receberam por email" style={{ marginLeft: 6, fontSize: '0.75rem', color: '#64748b' }}><LiquidText>✉ </LiquidText>{c.num_envios}</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {!c.importada && c.estado === 'rascunho' && (
                                                        <button data-liquid-primary="true" onClick={() => mudarEstado(c, 'ativo')} style={{ ...btnPri, ...mini }} title="Ativar para recolher respostas">Ativar</button>
                                                    )}
                                                    {!c.importada && c.estado === 'ativo' && (
                                                        <>
                                                            <button data-liquid-primary="true" onClick={() => setPartilha(c)} style={{ ...btnPri, ...mini }} title="Partilhar link">Partilhar</button>
                                                            <button data-liquid-secondary="true" onClick={() => mudarEstado(c, 'fechado')} style={{ ...btnSec, ...mini }} title="Fechar respostas">Fechar</button>
                                                        </>
                                                    )}
                                                    {!c.importada && c.estado === 'fechado' && (
                                                        <button data-liquid-secondary="true" onClick={() => mudarEstado(c, 'ativo')} style={{ ...btnSec, ...mini }} title="Reabrir">Reabrir</button>
                                                    )}
                                                    <button data-liquid-secondary="true" onClick={() => setResultados(c.id)} style={{ ...btnSec, ...mini }} disabled={!participacoes} title="Ver resultados">Resultados</button>
                                                    {!c.importada && <button data-liquid-secondary="true" onClick={() => setBuilder({ id: c.id })} style={{ ...btnSec, ...mini }} title="Editar">Editar</button>}
                                                    <button data-liquid-secondary="true" onClick={() => eliminar(c)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

            {builder && <BuilderModal consultaId={builder.id} ano={ano} alvo={alvo} onClose={() => setBuilder(null)} onSaved={carregar} />}
            {partilha && <PartilhaModal consulta={partilha} alvo={alvo} onClose={() => setPartilha(null)} onChanged={carregar} />}
            {resultados && <ResultadosModal consultaId={resultados} onClose={() => setResultados(null)} />}
            {importar && <ImportWizard ano={ano} onClose={() => setImportar(false)} onSaved={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const pgCard = { border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fcfcfd' };
const miniBtn = { border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: '0.75rem', padding: '3px 7px', color: '#475569' };
const presetBtn = { border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', padding: '4px 10px', color: '#475569', marginRight: 6 };
const temaTag = { fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 };
const infoBox = { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: '0.85rem' };
const dropdown = { position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 230, overflow: 'hidden' };
const dropItem = { display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' };

export default ConsultaTrabalhadores;
